// ---------------------------------------------------------------------------
// The dissemination-channel rules the form applies before a send (card FE-06, the front-end half
// of BE-12). Two server answers feed them:
//   GET /fusion/v2/fema/jurisdictions — per COG: the event-code grant per channel
//     (`eventCodesByChannel`), the EAS organization code (`easOrgCode`, decision D18) and the
//     channels each SAME code is granted on (`jurisdictions[].channels`).
//   GET /fusion/v2/fema/meta/type — static IDG facts: the codes each channel can carry
//     (`dissemination.eventCodesByChannel`, Tables 79/80) and the channel limits.
// Both answers are JSON that an older server may not carry, so every value is checked here.
// ---------------------------------------------------------------------------
import type {
  FemaAlertContract,
  FemaEasOrgCode,
  FemaEventCodesByChannel,
  FemaJurisdiction,
  FemaJurisdictions,
} from 'types/fema.types';
import { FEMA_DISSEMINATION_CHANNELS, FEMA_EAS_ORG_CODES } from 'types/fema.types';
import {
  deliveryChannelOptions,
  IPAWS_CHANNEL_EXPIRY_LIMITS_FALLBACK,
  isDeliveryChannelAvailableForEvent,
  type IpawsChannelExpiryLimits,
  type IpawsDeliveryChannel,
} from '../components/IpawsAlertFields/config/Ipaws.config';

export type IpawsChannelRules = {
  /** `meta/type` (IDG Tables 79/80). `null`: not served, so the local glossary limits apply. */
  channelEventCodes: FemaEventCodesByChannel | null;
  /**
   * `jurisdictions`: the COG grant per channel. `null`: unknown (not served, or served as an
   * empty record), so no grant check runs. The server reads an empty record the same way
   * (Backend `checkChannelAuthorization.ops.ts:45`).
   */
  cogEventCodes: FemaEventCodesByChannel | null;
  /**
   * `jurisdictions`: the EAS-ORG value. Read it with `easOrgCodeLoaded`: `null` in a loaded
   * answer means the COG profile has no code.
   */
  easOrgCode: FemaEasOrgCode | null;
  /**
   * `true` only when `/jurisdictions` answered and the answer carries the `easOrgCode` key. While
   * the answer loads, or when the request failed, the code is unknown.
   */
  easOrgCodeLoaded: boolean;
  /** `meta/type.dissemination.limits`, with the local numbers as the fallback. */
  expiryLimits: IpawsChannelExpiryLimits;
};

/**
 * The server applies the channel content rules and the COG grant checks only to an `Actual`
 * alert (IDG Table 76 status row; FEMA 301; 401/501/601). The form does the same. The static
 * Table 79/80 rule and the required fields apply at every status.
 */
export const appliesChannelGrantRules = (status: unknown): boolean => status === 'Actual';

type IpawsDeliveryChannelOption = (typeof deliveryChannelOptions)[number];

/** IDG v4.06 status code for a COG that lacks the geocode grant, per channel. */
const GEOCODE_GRANT_STATUS: Record<IpawsDeliveryChannel, string> = {
  NWEM: '419',
  EAS: '519',
  CMAS: '619',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const readPositiveNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;

/**
 * Reads a per-channel event-code map. Unknown channel keys and non-string codes are dropped. A
 * map with no channel left reads as `null` (unknown), never as "no grant on any channel".
 */
export const readEventCodesByChannel = (value: unknown): FemaEventCodesByChannel | null => {
  if (!isRecord(value)) return null;
  const result: FemaEventCodesByChannel = {};
  FEMA_DISSEMINATION_CHANNELS.forEach((channel) => {
    const codes = value[channel];
    if (isStringArray(codes)) result[channel] = codes;
  });
  return Object.keys(result).length > 0 ? result : null;
};

/** Reads an EAS organization code. Any value other than PEP, EAS, WXR or CIV reads as `null`. */
export const readEasOrgCode = (value: unknown): FemaEasOrgCode | null =>
  FEMA_EAS_ORG_CODES.find((code) => code === value) ?? null;

/** Builds the channel rules from the two server answers. Either answer can be missing. */
export const readIpawsChannelRules = (
  jurisdictions?: FemaJurisdictions | null,
  contract?: FemaAlertContract | null,
): IpawsChannelRules => {
  const dissemination: unknown = contract?.dissemination;
  const served = isRecord(dissemination) ? dissemination : {};
  const limits = isRecord(served.limits) ? served.limits : {};

  return {
    channelEventCodes: readEventCodesByChannel(served.eventCodesByChannel),
    cogEventCodes: readEventCodesByChannel(jurisdictions?.eventCodesByChannel),
    easOrgCode: readEasOrgCode(jurisdictions?.easOrgCode),
    easOrgCodeLoaded: isRecord(jurisdictions) && 'easOrgCode' in jurisdictions,
    expiryLimits: {
      easExpiresHours: readPositiveNumber(
        limits.easExpiresHours,
        IPAWS_CHANNEL_EXPIRY_LIMITS_FALLBACK.easExpiresHours,
      ),
      nwemExpiresHours: readPositiveNumber(
        limits.nwemExpiresHours,
        IPAWS_CHANNEL_EXPIRY_LIMITS_FALLBACK.nwemExpiresHours,
      ),
      cmasExpiresHours: readPositiveNumber(
        limits.cmasExpiresHours,
        IPAWS_CHANNEL_EXPIRY_LIMITS_FALLBACK.cmasExpiresHours,
      ),
    },
  };
};

/** EAS and NWEM carry the EAS-ORG parameter (IDG Table 16 p29; FEMA 402/502). */
export const needsEasOrgCode = (channel: string): boolean =>
  channel === 'EAS' || channel === 'NWEM';

const channelShortLabel = (channel: string): string =>
  deliveryChannelOptions.find(({ value }) => value === channel)?.shortLabel ?? channel;

/** The reason the COG grant refuses the event code on the channel, or '' when it allows it. */
const getEventCodeGrantReason = (
  channel: IpawsDeliveryChannelOption,
  eventCode: string,
  rules?: IpawsChannelRules | null,
): string => {
  if (!eventCode || !rules?.cogEventCodes) return '';
  if ((rules.cogEventCodes[channel.value] ?? []).includes(eventCode)) return '';
  return `This COG is not authorized for ${eventCode} on ${channel.shortLabel} (FEMA 415/515/615).`;
};

/**
 * Why the COG profile gives no EAS-ORG value, or '' when it gives one. "Not loaded" (the
 * `/jurisdictions` answer is loading, failed or older than card BE-12) and "no code" (a loaded
 * answer with `easOrgCode: null`) are two different facts.
 */
export const getEasOrgCodeProblem = (rules?: IpawsChannelRules | null): string => {
  if (rules?.easOrgCode) return '';
  return rules?.easOrgCodeLoaded
    ? 'the COG profile has no EAS organization code'
    : 'the COG profile has not loaded';
};

const getEasOrgCodeReason = (
  channel: IpawsDeliveryChannelOption,
  rules?: IpawsChannelRules | null,
): string => {
  const problem = needsEasOrgCode(channel.value) ? getEasOrgCodeProblem(rules) : '';
  return problem ? `${channel.shortLabel} cannot be used: ${problem}.` : '';
};

/**
 * Why a channel cannot be selected, or '' when it can. The order is: the channel cannot carry the
 * event code (IDG Tables 79/80), the COG has no EAS organization code (EAS and NWEM only), and
 * the COG has no grant for the event code on the channel. The last two apply only to an `Actual`
 * draft (`appliesChannelGrantRules`). An omitted `status` applies every rule.
 */
export const getDeliveryChannelUnavailableReason = (
  channel: IpawsDeliveryChannelOption,
  eventCode: string,
  rules?: IpawsChannelRules | null,
  status: string = 'Actual',
): string => {
  if (!isDeliveryChannelAvailableForEvent(channel, eventCode, rules?.channelEventCodes)) {
    return `${channel.shortLabel} cannot be used with ${eventCode}.`;
  }
  if (!appliesChannelGrantRules(status)) return '';
  return getEasOrgCodeReason(channel, rules) || getEventCodeGrantReason(channel, eventCode, rules);
};

/**
 * The COG-grant reasons for the channels the draft selected (the static Table 79/80 finding has
 * its own message in `getIpawsDraftFindings`). None for a draft that is not `Actual`.
 */
export const getSelectedChannelGrantFindings = (
  draft: { status?: string; eventCode?: string; deliveryChannels?: string[] },
  rules?: IpawsChannelRules | null,
): string[] => {
  if (!appliesChannelGrantRules(draft.status)) return [];
  const selected = Array.isArray(draft.deliveryChannels) ? draft.deliveryChannels : [];
  return deliveryChannelOptions
    .filter(({ value }) => selected.includes(value))
    .map((channel) => getEventCodeGrantReason(channel, draft.eventCode ?? '', rules))
    .filter(Boolean);
};

/** The selected channels that the area's SAME code is not granted on. */
export const getChannelsMissingForArea = (
  jurisdiction: Pick<FemaJurisdiction, 'channels'> | null | undefined,
  selectedChannels?: readonly string[],
): IpawsDeliveryChannel[] => {
  if (!jurisdiction || !Array.isArray(selectedChannels)) return [];
  const granted = Array.isArray(jurisdiction.channels) ? jurisdiction.channels : [];
  return deliveryChannelOptions
    .map(({ value }) => value)
    .filter((channel) => selectedChannels.includes(channel) && !granted.includes(channel));
};

/** The short channel names, joined for a sentence: "WEA", "WEA and EAS". */
export const formatChannelNames = (channels: readonly string[]): string => {
  const names = channels.map(channelShortLabel);
  return names.length > 1
    ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    : names.join('');
};

/**
 * One finding per selected channel that an area of the draft is not granted on (IDG §8.4 p64;
 * FEMA 419/519/619). A SAME code the COG does not hold at all has its own finding
 * (`unauthorizedGeocodeFinding`). None for a draft that is not `Actual`.
 */
export const getChannelAreaFindings = (
  draft: {
    status?: string;
    deliveryChannels?: string[];
    geocodes?: { valueName?: string; value?: string }[];
  },
  jurisdictions?: readonly FemaJurisdiction[] | null,
): string[] => {
  if (!appliesChannelGrantRules(draft.status)) return [];
  if (!Array.isArray(jurisdictions) || !jurisdictions.length) return [];
  const sameCodes = (draft.geocodes ?? [])
    .filter((code) => code?.valueName === 'SAME' && code.value)
    .map(({ value }) => value ?? '');

  return sameCodes.flatMap((sameCode) => {
    const jurisdiction = jurisdictions.find((item) => item?.sameCode === sameCode);
    return getChannelsMissingForArea(jurisdiction, draft.deliveryChannels).map(
      (channel) =>
        `SAME ${sameCode} is not authorized on ${channelShortLabel(channel)} for this COG ` +
        `(FEMA ${GEOCODE_GRANT_STATUS[channel]}). Pick another area, or clear ${channelShortLabel(channel)}.`,
    );
  });
};
